import React, { useMemo, useState, useEffect } from 'react';
import styles from './Table.module.css';

export interface Column<T = any> {
  Header?: string;
  accessor?: string;
  sortable?: boolean;
  Cell?: (props: { row: T; value: any }) => React.ReactNode;
  format?: (value: any) => string;
}

interface TableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  sortable?: boolean;
  hoverable?: boolean;
  emptyMessage?: string;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
}

/**
 * Standard Table Component
 */
const Table = <T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  sortable = true,
  hoverable = true,
  emptyMessage = 'No data available',
  pageSize: initialPageSize = 10,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}: TableProps<T>) => {
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' }>({
    key: null,
    direction: 'asc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  // Reset sort when columns change
  useEffect(() => {
    setSortConfig({ key: null, direction: 'asc' });
  }, [columns]);

  // Handle column sort
  const handleSort = (columnKey: string) => {
    if (!sortable || !columnKey) return;

    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  // Sort data based on current sort config
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      const key = sortConfig.key!;
      let aVal = a[key];
      let bVal = b[key];

      // Handle null/undefined
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Handle numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle dates (simple heuristic or use Date object if possible)
      // Ideally data should be normalized before passing to Table, but we can try to parse
      const dateA = new Date(aVal);
      const dateB = new Date(bVal);
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime()) && typeof aVal !== 'number') {
        return sortConfig.direction === 'asc'
          ? dateA.getTime() - dateB.getTime()
          : dateB.getTime() - dateA.getTime();
      }

      // Handle strings
      const strA = String(aVal).toLowerCase();
      const strB = String(bVal).toLowerCase();

      if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, pageSize]);

  // Calculate total pages
  const totalPages = Math.ceil(sortedData.length / pageSize);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  // Render sort indicator
  const renderSortIndicator = (columnKey: string) => {
    if (!sortable || !columnKey || sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Format cell value
  const formatCellValue = (value: any, column: Column) => {
    if (value === null || value === undefined) return '-';

    // Check if column has custom formatter
    if (column.format) {
      return column.format(value);
    }

    return value;
  };

  if (!data || data.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={col.accessor || idx}
                  onClick={() => sortable && col.sortable !== false && col.accessor && handleSort(col.accessor)}
                  className={sortable && col.sortable !== false && col.accessor ? styles.sortable : ''}
                >
                  {col.Header}
                  {col.accessor && renderSortIndicator(col.accessor)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIdx) => (
              <tr
                key={row.id || row.job_id || rowIdx}
                onClick={() => onRowClick && onRowClick(row)}
                className={onRowClick ? styles.clickable : ''}
              >
                {columns.map((col, colIdx) => (
                  <td key={col.accessor || colIdx} className={styles.tableCell}>
                    {col.Cell
                      ? col.Cell({ row, value: col.accessor ? row[col.accessor] : undefined })
                      : formatCellValue(col.accessor ? row[col.accessor] : undefined, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {sortedData.length > 0 && (
        <div className={styles.paginationContainer}>
          <div className={styles.paginationInfo}>
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} results
          </div>

          <div className={styles.paginationControls}>
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className={styles.paginationButton}
              aria-label="First page"
            >
              ««
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={styles.paginationButton}
              aria-label="Previous page"
            >
              «
            </button>

            {getPageNumbers().map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className={styles.ellipsis}>
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page as number)}
                  className={`${styles.paginationButton} ${currentPage === page ? styles.active : ''}`}
                  aria-label={`Page ${page}`}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </button>
              )
            )}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={styles.paginationButton}
              aria-label="Next page"
            >
              »
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className={styles.paginationButton}
              aria-label="Last page"
            >
              »»
            </button>
          </div>

          <div className={styles.pageSizeSelector}>
            <label htmlFor="pageSize">Show:</label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className={styles.pageSizeSelect}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>per page</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
